import { JsonFormsLayout } from './JsonFormLayout';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { rankWith, uiTypeIs } from '@jsonforms/core';
import type { JsonSchema, RankedTester, RendererProps, Layout, UISchemaElement } from '@jsonforms/core';
import type { Labeled } from '@jsonforms/core/src/models/uischema';
import { withJsonFormsLayoutProps } from '@jsonforms/react';
import { cn } from '@thinkalpha/ui-core/utils';
import React, { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'src/components/ErrorBoundary';
import { gridClasses } from 'src/components/JsonForm/layouts/GridLayout';
import { RenderChildren } from 'src/components/JsonForm/RenderChildren';
import { Button } from 'src/components/ui';

export interface GridCollapsibleLayout extends Layout, Labeled {
    type: 'GridCollapsibleLayout';
}

export const GridCollapsibleLayoutTester: RankedTester = rankWith(10, uiTypeIs('GridCollapsibleLayout'));

export const MAX_GROUP_RENDER_RETRIES = 3;

/**
 * Contains render crashes of the group's fields so a broken control inside a collapsible
 * section (e.g. malformed schema values from a vendor spec) degrades only this section
 * instead of taking down the whole form. Retries when the schema or uischema changes, up
 * to a budget per expansion: unrelated form edits regenerate the uischema with a fresh
 * reference, so an uncapped retry would re-crash (and re-log) a persistently broken group
 * on every edit. Collapsing and re-expanding resets the budget and retries.
 */
const CollapsibleContentErrorFallback = ({
    resetErrorBoundary,
    schema,
    uischema,
    retryCountRef,
}: {
    resetErrorBoundary: () => void;
    schema: JsonSchema;
    uischema: UISchemaElement;
    retryCountRef: React.RefObject<number>;
}) => {
    const schemaAtErrorRef = useRef(schema);
    const uischemaAtErrorRef = useRef(uischema);
    useEffect(() => {
        if (
            (schema !== schemaAtErrorRef.current || uischema !== uischemaAtErrorRef.current) &&
            retryCountRef.current < MAX_GROUP_RENDER_RETRIES
        ) {
            retryCountRef.current += 1;
            resetErrorBoundary();
        }
    }, [schema, uischema, resetErrorBoundary, retryCountRef]);

    return (
        <div className="py-2 text-center text-xs text-ember-400" data-testid="collapsible-group-error">
            These fields cannot be displayed.
        </div>
    );
};

export const GridCollapsibleLayoutStableRenderer = (props: RendererProps) => {
    const { data: _data, ...otherProps } = props;
    // We don't hand over data to the layout renderer to avoid rerendering it with every data change
    return <GridCollapsibleLayoutRendererMemoizedComponent {...otherProps} />;
};

const GridCollapsibleLayoutRendererComponent = ({ schema, uischema, enabled, visible, path }: RendererProps) => {
    const layout = uischema as GridCollapsibleLayout;

    const visibleElements = layout.elements.filter((element) => element?.rule?.effect !== 'HIDE');

    const [open, setOpen] = useState(false);

    // Auto-retry budget for the error fallback; a collapse/expand cycle grants a fresh one
    const retryCountRef = useRef(0);

    return visible && visibleElements.length > 0 ? (
        <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-1.5">
                <div className="h-px w-full bg-gray-500" />
                <div>
                    <Button
                        onClick={() => {
                            retryCountRef.current = 0;
                            setOpen(!open);
                        }}
                        variant="ghost"
                        size="xs"
                        className="h-6 px-1.5 text-[8px] text-gray-400 uppercase"
                    >
                        <span>{layout.label}</span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={cn('ml-1.5 h-2 w-2 shrink-0 transition-transform', !open && 'rotate-90')}
                        />
                    </Button>
                </div>
            </div>
            {open && layout.elements.length > 0 && (
                <ErrorBoundary
                    title="GridCollapsibleLayout"
                    fallback={({ resetErrorBoundary }) => (
                        <CollapsibleContentErrorFallback
                            resetErrorBoundary={resetErrorBoundary}
                            schema={schema}
                            uischema={uischema}
                            retryCountRef={retryCountRef}
                        />
                    )}
                >
                    <JsonFormsLayout
                        className={gridClasses}
                        visible={visible}
                        enabled={enabled}
                        path={path}
                        uischema={uischema}
                        schema={schema}
                    >
                        <RenderChildren
                            layout={layout}
                            schema={schema}
                            path={path}
                            enabled={enabled}
                            className="[&:empty]:hidden"
                        />
                    </JsonFormsLayout>
                </ErrorBoundary>
            )}
        </div>
    ) : null;
};

const GridCollapsibleLayoutRendererMemoizedComponent = React.memo(GridCollapsibleLayoutRendererComponent);

export const GridCollapsibleLayoutRenderer = withJsonFormsLayoutProps(GridCollapsibleLayoutStableRenderer, false);
